import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  LinearProgress,
  Avatar,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';
import { Search as SearchIcon, FilterList as FilterIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProfileCompletionPrompt from '../../components/ProfileCompletionPrompt';
// getAvatarUrl скопирован из MainHeader
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return null;
  if (avatarPath.startsWith('http')) return avatarPath;
  const baseUrl = API_BASE.replace('/api', '');
  return `${baseUrl}${avatarPath}`;
};

export default function DashboardHome() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [profileSurveys, setProfileSurveys] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  
  // Filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('reward');
  const [showCompleted, setShowCompleted] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  // Deprecated respondent-level setting removed; now controlled per survey
  const [questionPageSize, setQuestionPageSize] = useState(1);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const translateCategory = (key) => {
    if (!key) return '';
    const map = {
      'online-surveys': 'Онлайн-опросы',
      'online-interviews': 'Онлайн-интервью и фокус-группы',
      'website-app-testing': 'Тест сайтов и приложений',
      'store-visits': 'Визиты в магазины и офисы',
      'product-testing': 'Тестирование продуктов и услуг',
      'other': 'Другое',
      // Старые категории для обратной совместимости
      general: 'Онлайн-опросы',
      market: 'Онлайн-опросы',
      'market-research': 'Онлайн-опросы',
    };
    return map[key] || key;
  };

  useEffect(() => {
    fetchData();
    fetchProfileSurveys();
    fetchProfileCompletion();
  }, []);

  useEffect(() => {
    fetchSurveys();
  }, [searchQuery, page, sortBy]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Получаем свежие данные пользователя с актуальным балансом
      const userRes = await axios.get(`${API_BASE}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userRes.data);
      
      // Обновляем localStorage актуальными данными
      localStorage.setItem('user', JSON.stringify(userRes.data));

      // Индивидуальная настройка режима прохождения перенесена на уровень опроса (questionsPerPage)
      setQuestionPageSize(1);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchProfileSurveys = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/surveys?profileSurveysOnly=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = res.data.surveys || res.data;
      setProfileSurveys(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Error fetching profile surveys:', e);
    }
  };

  const fetchProfileCompletion = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/surveys/profile/completion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileCompletion(res.data);
    } catch (e) {
      console.error('Error fetching profile completion:', e);
    }
  };

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        status: 'active',
        page: page.toString(),
        limit: '20'
      });
      if (searchQuery) params.append('search', searchQuery);
      
      // Map sortBy to server field
      let sortField = 'createdAt';
      let sortOrder = 'desc';
      if (sortBy === 'reward') {
        sortField = 'reward.amount';
        sortOrder = 'desc';
      } else if (sortBy === 'time') {
        sortField = 'estimatedTime';
        sortOrder = 'asc';
      }
      params.append('sortBy', sortField);
      params.append('sortOrder', sortOrder);

      const res = await axios.get(`${API_BASE}/surveys?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both old and new response formats
      if (res.data.surveys) {
        setSurveys(res.data.surveys);
        setPagination(res.data.pagination || { page: 1, limit: 20, total: res.data.surveys.length, totalPages: 1 });
      } else {
        // Fallback for old format
        setSurveys(res.data);
        setPagination({ page: 1, limit: 20, total: res.data.length, totalPages: 1 });
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
      
      // Проверяем на ошибку незаполненного профиля
      if (error.response?.status === 403 && error.response?.data?.error === 'incomplete_profile') {
        setProfileIncomplete(true);
        setMissingFields(error.response.data.missingFields || []);
      }
    } finally {
      setLoading(false);
    }
  };

  // Client-side filters: optional hiding of completed
  let filteredSurveys = surveys.filter(survey => {
    const matchesCompleted = showCompleted ? true : !survey.alreadyCompleted;
    return matchesCompleted;
  });
  // When showing completed, push them to the bottom for better UX
  if (showCompleted) {
    filteredSurveys = [...filteredSurveys].sort((a, b) => {
      const aC = a.alreadyCompleted ? 1 : 0;
      const bC = b.alreadyCompleted ? 1 : 0;
      return aC - bC; // non-completed first
    });
  }

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Profile Summary */}
      {user && (
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 1.5, md: 4 }, 
            mb: 2,
            mx: { xs: 0, md: 3 },
            mt: 0,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '32px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                alt={user.fullName || user.email}
                src={getAvatarUrl(user.avatar)}
                sx={{ 
                  width: 80, 
                  height: 80,
                  background: 'linear-gradient(135deg, #667eea 0%, #74b9ff 100%)',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                }}
              >
                {(user.fullName || user.email || 'U').charAt(0).toUpperCase()}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#2d3748' }}>
                {user.fullName || user.email}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: '#718096' }}>
                {user.email}
              </Typography>
            </Grid>
            <Grid item>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2.5, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 234, 167, 0.5)',
                  boxShadow: '0 4px 16px rgba(253, 203, 110, 0.3)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748' }}>
                  {Number(user.points || 0).toFixed(1)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#744210', fontWeight: 500 }}>
                  Баллы
                </Typography>
              </Paper>
            </Grid>
            <Grid item>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2.5, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  borderRadius: '20px',
                  border: '1px solid rgba(116, 185, 255, 0.5)',
                  boxShadow: '0 4px 16px rgba(9, 132, 227, 0.3)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                  {(user.balance || 0).toFixed(2)} BYN
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                  Баланс
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Search and Filters */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 2,
          mx: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Поиск опросов"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Сортировка</InputLabel>
              <Select
                value={sortBy}
                label="Сортировка"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="reward">Высокая награда</MenuItem>
                <MenuItem value="time">Короткое время</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <FormControlLabel
                  control={<Switch checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />}
                  label="Показывать пройденные"
                />
              </Box>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Profile Surveys Section */}
      {profileSurveys.length > 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            mb: 2,
            mx: 3,
            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
            borderRadius: '32px',
            border: '1px solid rgba(116, 185, 255, 0.5)',
            boxShadow: '0 8px 32px rgba(9, 132, 227, 0.3)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>
              Обязательные профильные опросы
            </Typography>
            {profileCompletion && (
              <Chip 
                label={`${profileCompletion.completedProfileSurveys}/${profileCompletion.totalProfileSurveys} завершено`}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  color: '#0984e3',
                  fontWeight: 'bold',
                  borderRadius: '16px',
                }}
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.95)' }}>
            Заполните профильные опросы, чтобы получить доступ к большему количеству опросов.
            <br />
            <strong>За первое прохождение каждого профильного опроса: +0.3 балла</strong>
          </Typography>
          <Grid container spacing={2}>
            {profileSurveys.map(survey => (
              <Grid item xs={12} md={6} key={survey.id}>
                <Card 
                  elevation={0}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '24px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
                    }
                  }}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#2d3748' }}>
                        {survey.title}
                      </Typography>
                      <Chip 
                        label="Профиль" 
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                          color: '#fff',
                          borderRadius: '12px',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, color: '#718096' }}>
                      {survey.description}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#a0aec0' }}>
                      ⏱️ {survey.estimatedTime || 5} мин • {survey.questions?.length || 0} вопросов
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button 
                      fullWidth
                      variant="contained"
                      onClick={() => navigate(`/respondent/surveys/${survey.id}`)}
                      sx={{
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        borderRadius: '16px',
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1,
                        boxShadow: '0 4px 16px rgba(9, 132, 227, 0.4)',
                        '&:hover': {
                          boxShadow: '0 6px 24px rgba(9, 132, 227, 0.5)',
                        }
                      }}
                    >
                      Начать
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Available Surveys */}
      <Box sx={{ px: 3, mb: 2 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 2, 
            fontWeight: 700,
            color: '#2d3748',
          }}
        >
          Доступные опросы
        </Typography>
        <Grid container spacing={2}>
          {filteredSurveys.length === 0 ? (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '24px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <Typography sx={{ color: '#718096', fontSize: '1.1rem' }}>
                  Нет доступных опросов
                </Typography>
              </Paper>
            </Grid>
          ) : (
            filteredSurveys.map((survey) => {
              return (
                <Grid item xs={12} md={6} key={survey.id}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      opacity: survey.alreadyCompleted ? 0.7 : 1,
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '24px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: survey.alreadyCompleted ? 'none' : 'translateY(-4px)',
                        boxShadow: survey.alreadyCompleted ? '0 8px 32px rgba(0, 0, 0, 0.1)' : '0 12px 40px rgba(0, 0, 0, 0.15)',
                      }
                    }}
                  >
                    <CardContent>
                      {survey.allowMultipleResponses && (
                        <Chip 
                          label="Можно пройти несколько раз" 
                          size="small"
                          sx={{
                            background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
                            color: '#fff',
                            borderRadius: '12px',
                            fontWeight: 600,
                            mb: 1,
                          }}
                        />
                      )}
                      {survey.isExternalSurvey && (
                        <Chip 
                          label="Внешний опрос" 
                          size="small"
                          sx={{
                            background: 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)',
                            color: '#fff',
                            borderRadius: '12px',
                            fontWeight: 600,
                            mb: 1,
                          }}
                        />
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2d3748' }}>
                          {survey.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {survey.alreadyCompleted && (
                            <Chip 
                              label="Пройден" 
                              size="small"
                              sx={{
                                background: 'linear-gradient(135deg, #55efc4 0%, #00b894 100%)',
                                color: '#fff',
                                borderRadius: '12px',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Typography sx={{ mb: 2, color: '#718096' }}>
                        {survey.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {(() => {
                        const r = survey.reward || {};
                        const money = typeof r.moneyAmount === 'number' ? r.moneyAmount
                                      : (r.type === 'money' ? (r.amount || 0) : (r.money?.amount || 0));
                        const points = typeof r.pointsAmount === 'number' ? r.pointsAmount
                                       : (r.type === 'points' ? (r.amount || 0) : (r.points?.amount || 0));
                        return (
                          <>
                            {money > 0 && (
                              <Chip
                                label={`${Number(money).toFixed(1)} BYN`}
                                icon={<span>💰</span>}
                                size="small"
                                sx={{
                                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                                  color: '#fff',
                                  borderRadius: '12px',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                            {points > 0 && (
                              <Chip
                                label={`${Number(points).toFixed(1)} баллов`}
                                icon={<span>🎁</span>}
                                size="small"
                                sx={{
                                  background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                                  color: '#744210',
                                  borderRadius: '12px',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </>
                        );
                      })()}
                      <Chip
                        label={`${survey.estimatedTime || 'Н/Д'} мин`}
                        icon={<span>⏱️</span>}
                        size="small"
                        sx={{
                          bgcolor: '#f7fafc',
                          color: '#718096',
                          borderRadius: '12px',
                          fontWeight: 500,
                        }}
                      />
                      {!survey.isExternalSurvey && (
                      <Chip
                        label={`${survey.questions?.length || 0} вопросов`}
                        icon={<span>📋</span>}
                        size="small"
                        sx={{
                          bgcolor: '#f7fafc',
                          color: '#718096',
                          borderRadius: '12px',
                          fontWeight: 500,
                        }}
                      />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button 
                      fullWidth
                      variant="contained"
                      onClick={() => navigate(`/respondent/surveys/${survey.id}`)}
                      disabled={survey.alreadyCompleted}
                      sx={{
                        background: survey.alreadyCompleted 
                          ? '#cbd5e0' 
                          : 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        borderRadius: '16px',
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1,
                        color: '#fff',
                        boxShadow: survey.alreadyCompleted ? 'none' : '0 4px 16px rgba(9, 132, 227, 0.4)',
                        '&:hover': {
                          boxShadow: survey.alreadyCompleted ? 'none' : '0 6px 24px rgba(9, 132, 227, 0.5)',
                        }
                      }}
                    >
                      {survey.alreadyCompleted ? 'Уже пройден' : 'Подробнее'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })
        )}
        </Grid>
      </Box>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box sx={{ px: 3 }}>
          <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#2d3748',
                },
                '& .Mui-selected': {
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  color: '#fff',
                }
              }}
            />
          </Stack>
        </Box>
      )}

      {/* Profile Completion Prompt */}
      <ProfileCompletionPrompt
        open={profileIncomplete}
        missingFields={missingFields}
        onClose={() => setProfileIncomplete(false)}
      />
    </Box>
  );
}